import React from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Avatar, Box, Button, Container, CssBaseline, Typography } from "@material-ui/core";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import { makeStyles } from "@material-ui/core/styles";
import { UserContext } from "contexts/UserContext";
import { USER_KEY } from "CONSTANTS";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4, 2),
  },
  content: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  avatar: { margin: theme.spacing(1), backgroundColor: theme.palette.secondary.main },
  spaceAfter: { marginBottom: 15 },
  button: { marginTop: theme.spacing(3) },
}));

// Where the link emailed at sign-up lands (UserAccountAdapter.get_email_
// confirmation_url). Opening the page changes nothing; confirming is a button
// press (a POST), so an email scanner or link preview can't confirm an address
// on someone's behalf. Public - the signed key in the link is the proof and it
// may be opened on any device.
export const ConfirmEmail = () => {
  const classes = useStyles();
  const { key } = useParams();
  const userContext = React.useContext(UserContext);
  // ready | working | confirmed | invalid
  const [phase, setPhase] = React.useState("ready");
  const [error, setError] = React.useState("");

  const confirm = () => {
    setPhase("working");
    setError("");
    axios
      .post("/auth/registration/verify-email/", { key })
      .then(() => {
        // If they're signed in here, drop the reminder right away.
        const user = userContext.user;
        if (user.isAuthenticated) {
          const confirmed = { ...user, email_verified: true };
          localStorage.setItem(USER_KEY, JSON.stringify(confirmed));
          userContext.setUser(confirmed);
        }
        setPhase("confirmed");
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          setPhase("invalid");
          return;
        }
        setError("Something went wrong - try again.");
        setPhase("ready");
      });
  };

  return (
    <Box className={classes.root} component="main">
      <CssBaseline />
      <Container maxWidth="xs">
        <div className={classes.content}>
          <Avatar className={classes.avatar}>
            <MailOutlineIcon />
          </Avatar>

          {(phase === "ready" || phase === "working") && (
            <>
              <Typography component="h1" variant="h5" className={classes.spaceAfter}>
                Confirm your email
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Press the button to confirm this is your address.
              </Typography>
              {error && (
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              )}
              <Button
                className={classes.button}
                variant="contained"
                color="secondary"
                onClick={confirm}
                disabled={phase === "working"}
              >
                Confirm my email
              </Button>
            </>
          )}

          {phase === "confirmed" && (
            <>
              <Typography component="h1" variant="h5" className={classes.spaceAfter}>
                Thanks - you're confirmed
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Your email address is confirmed.
              </Typography>
              <Button className={classes.button} variant="contained" color="primary" href="/">
                Continue
              </Button>
            </>
          )}

          {phase === "invalid" && (
            <>
              <Typography component="h1" variant="h5" className={classes.spaceAfter}>
                This link isn't valid
              </Typography>
              <Typography variant="body1" color="textSecondary">
                It has expired or isn't right. Sign in and use "Resend" in the reminder at
                the top to get a new one.
              </Typography>
              <Button className={classes.button} variant="contained" color="primary" href="/login">
                Sign in
              </Button>
            </>
          )}
        </div>
      </Container>
    </Box>
  );
};
